import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { type InputHTMLAttributes, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, type EntrySuggestion } from '../lib/api'
import { computeDropdownPlacement } from '../lib/dropdown-placement'
import { OUTSIDE_CLICK_EXEMPT_ATTR } from '../lib/outside-click'
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '../lib/use-debounced-value'
import { useVisualViewportRect } from '../lib/use-visual-viewport'
import { Input } from './ui/input'

/** Dropdown erst ab 2 Zeichen — deckt sich mit dem Server-Early-Return. */
const MIN_QUERY_LENGTH = 2
const MAX_SUGGESTIONS = 8
/** Abstand Dropdown↔Anker. */
const ANCHOR_GAP = 4

interface DescriptionAutocompleteProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onSelect'> {
  value: string
  onChange: (next: string) => void
  // Fired when the user picks a suggestion — gives the parent the chance to
  // fill project/service besides the description (only-empty-fields guard
  // lives in the parent via resolveSuggestionFill). Defaults to just writing
  // the description to value.
  onPick?: (suggestion: EntrySuggestion) => void
  /** Zum Auflösen der dezenten Projekt/Tätigkeits-Zeile im Dropdown. */
  projects?: { id: string; name: string }[]
  services?: { id: string; name: string }[]
}

// Clockodo-artige Titel-Vorschläge fürs Beschreibungsfeld: zeigt distinct
// Beschreibungen aus dem eigenen Verlauf, die den Tipptext enthalten. Das
// Dropdown ist reine Empfehlung — Free-Text bleibt immer erlaubt. Lazy ab
// Fokus, serverseitige Suche pro (debouncetem) Tastendruck; keepPreviousData
// hält die alte Liste sichtbar, bis die neue da ist (kein Flackern).
//
// Das Dropdown wird per Portal an document.body mit position:fixed gerendert,
// NICHT als absolute-Kind: sonst klippt es der overflow-y-auto-Scrollbody des
// mobilen Bottom-Sheets (SidePanel) weg — genau der iOS-Bug, bei dem im
// verkürzten Sheet (Tastatur offen) gar kein Dropdown erschien.
export function DescriptionAutocomplete({
  value,
  onChange,
  onPick,
  projects,
  services,
  className,
  ...rest
}: DescriptionAutocompleteProps) {
  const [focused, setFocused] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const q = value.trim()
  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS)
  const { data } = useQuery({
    queryKey: ['entry-suggestions', debouncedQ],
    queryFn: () => api.getEntrySuggestions(debouncedQ, MAX_SUGGESTIONS),
    // lazy: erst ab Fokus + genug Zeichen — kein Query pro Formular-Mount
    enabled: focused && debouncedQ.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
  // Gate auf den LIVE-Wert: wird der Text unter 2 Zeichen gelöscht, schließt
  // das Dropdown sofort statt die letzte (stale) Liste weiterzuzeigen.
  const suggestions = q.length >= MIN_QUERY_LENGTH ? (data?.suggestions ?? []) : []

  const open = focused && suggestions.length > 0

  // Anker-Rect live nachführen — das gefixte Portal-Dropdown scrollt/bewegt
  // sich nicht mit dem Anker mit, also bei Scroll/Resize/Tastatur neu messen.
  const viewport = useVisualViewportRect()
  const [rect, setRect] = useState<DOMRect | null>(null)
  const measure = useCallback(() => {
    setRect(inputRef.current?.getBoundingClientRect() ?? null)
  }, [])
  useLayoutEffect(() => {
    if (!open) return
    measure()
    let frame = 0
    const onChangeGeom = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }
    // capture:true fängt auch das Scrollen IM Sheet-Scrollbody ab.
    window.addEventListener('scroll', onChangeGeom, true)
    window.addEventListener('resize', onChangeGeom)
    window.visualViewport?.addEventListener('resize', onChangeGeom)
    window.visualViewport?.addEventListener('scroll', onChangeGeom)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onChangeGeom, true)
      window.removeEventListener('resize', onChangeGeom)
      window.visualViewport?.removeEventListener('resize', onChangeGeom)
      window.visualViewport?.removeEventListener('scroll', onChangeGeom)
    }
  }, [open, measure])

  const placement =
    rect && computeDropdownPlacement(rect.top, rect.bottom, viewport.height, viewport.offsetTop)
  const openUp = placement?.openUp ?? false

  return (
    <span className="relative inline-block w-full">
      <Input
        {...rest}
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => {
          if (blurTimer.current) clearTimeout(blurTimer.current)
          setFocused(true)
          rest.onFocus?.(e)
        }}
        onBlur={(e) => {
          // Defer the close so a click on a suggestion item registers before
          // the dropdown disappears. 120ms is enough for the mouseup to fire.
          blurTimer.current = setTimeout(() => setFocused(false), 120)
          rest.onBlur?.(e)
        }}
        className={className}
        autoComplete="off"
      />
      {open &&
        rect &&
        placement &&
        createPortal(
          <div
            role="listbox"
            aria-label="Titel-Vorschläge"
            // Markiert das Portal für Outside-Click-Handler umgebender
            // Anchor-Popovers (z.B. CalendarTimerButton) als „innen" — es
            // hängt an document.body und läge sonst außerhalb von deren
            // containerRef. Siehe lib/outside-click.ts; nicht entfernen.
            {...{ [OUTSIDE_CLICK_EXEMPT_ATTR]: '' }}
            className="fixed z-[60] overflow-y-auto rounded-lg border border-border-default bg-bg-elevated shadow-xl divide-y divide-border-subtle"
            style={{
              left: rect.left,
              width: rect.width,
              maxHeight: placement.maxHeight,
              // fixed teilt sich den Koordinatenraum mit getBoundingClientRect
              // (Layout-Viewport): für openUp die Unterkante über dem Anker
              // ankern, sonst die Oberkante unter dem Anker.
              ...(openUp
                ? { bottom: window.innerHeight - rect.top + ANCHOR_GAP }
                : { top: rect.bottom + ANCHOR_GAP }),
            }}
            // Don't let mousedown inside the dropdown blur the parent input —
            // otherwise the click would never reach the suggestion button.
            onMouseDown={(e) => e.preventDefault()}
          >
            {suggestions.map((s) => {
              const projectName = s.projectId
                ? projects?.find((p) => p.id === s.projectId)?.name
                : undefined
              const serviceName = s.serviceId
                ? services?.find((sv) => sv.id === s.serviceId)?.name
                : undefined
              const context = [projectName, serviceName].filter(Boolean).join(' · ')
              return (
                <button
                  key={s.description}
                  type="button"
                  onClick={() => {
                    if (onPick) onPick(s)
                    else onChange(s.description)
                    setFocused(false)
                  }}
                  // touch:min-h-11 hebt die Zeilen am Touch-Gerät auf >=44px Tap-Ziel
                  className="w-full flex items-start gap-2.5 px-3 py-2 touch:min-h-11 text-left text-sm hover:bg-bg-overlay/60 transition-colors"
                >
                  <History className="h-3.5 w-3.5 text-text-tertiary shrink-0 mt-0.5" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-medium text-text-primary">
                      {s.description}
                    </span>
                    {context && (
                      <span className="block truncate text-xs text-text-tertiary">{context}</span>
                    )}
                  </span>
                  {s.count > 1 && (
                    <span className="shrink-0 mt-0.5 text-2xs tabular-nums text-text-tertiary">
                      {s.count}×
                    </span>
                  )}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </span>
  )
}
